/**
 * LES RÉGLAGES DE L'ANALYSE — lot 99, 18/09/2026.
 *
 * Une ligne par société (`reglages_analyse`). Le BFR se sert des six premiers,
 * la trésorerie (lot 100) des cinq derniers — la commission du factor y a été
 * ajoutée au lot 100. Les valeurs de départ sont des
 * HYPOTHÈSES du cadrage, marquées « supposé » tant que personne ne les a
 * confirmées en enregistrant.
 */

import { useEffect, useState } from 'react';
import type { EchecApi } from './api.js';
import { enregistrerReglagesAnalyse, lireReglagesAnalyse } from './apiAnalyse.js';
import type { ReglagesAnalyse } from './bfr.js';

type Cle = Exclude<keyof ReglagesAnalyse, 'modifie_par' | 'modifie_le'>;

interface Champ {
  readonly cle: Cle;
  readonly nom: string;
  readonly unite: string;
  readonly aide: string;
  readonly sorte: 'entier' | 'montant';
  readonly min?: number;
  readonly max?: number;
}

export const CHAMPS_REGLAGES: readonly Champ[] = [
  {
    cle: 'jour_tva',
    nom: 'Jour de paiement de la TVA',
    unite: 'du mois',
    aide: 'TVA du mois précédent',
    sorte: 'entier',
    min: 1,
    max: 31,
  },
  {
    cle: 'charges_sociales',
    nom: 'Charges sociales du mois',
    unite: '€',
    aide: 'Stafa n’a pas la paie : montant à saisir',
    sorte: 'montant',
  },
  {
    cle: 'jour_charges_sociales',
    nom: 'Jour de paiement des charges sociales',
    unite: 'du mois',
    aide: 'charges du mois précédent',
    sorte: 'entier',
    min: 1,
    max: 31,
  },
  {
    cle: 'delai_facture_affretement',
    nom: 'Délai de réception de la facture d’affrètement',
    unite: 'jours après livraison',
    aide: 'supposé',
    sorte: 'entier',
    min: 0,
    max: 180,
  },
  {
    cle: 'delai_paiement_affretement',
    nom: 'Délai de paiement de l’affrètement',
    unite: 'jours après la facture',
    aide: 'supposé',
    sorte: 'entier',
    min: 0,
    max: 180,
  },
  {
    cle: 'delai_client',
    nom: 'Délai client d’un transport non facturé',
    unite: 'jours après fin de mois',
    aide: 'supposé, plus le retard habituel du client',
    sorte: 'entier',
    min: 0,
    max: 180,
  },
  {
    cle: 'retenue_factor',
    nom: 'Retenue du factor',
    unite: '%',
    aide: 'trésorerie · supposé',
    sorte: 'montant',
    max: 100,
  },
  {
    cle: 'commission_factor',
    nom: 'Commission du factor',
    unite: '% du TTC cédé',
    aide: 'trésorerie · à saisir, 0 = non comptée',
    sorte: 'montant',
    max: 100,
  },
  {
    cle: 'delai_cession',
    nom: 'Délai de cession au factor',
    unite: 'jours après la facture',
    aide: 'trésorerie · supposé',
    sorte: 'entier',
    min: 0,
    max: 60,
  },
  {
    cle: 'minimum_tresorerie',
    nom: 'Trésorerie minimum',
    unite: '€',
    aide: 'trésorerie · seuil d’alerte',
    sorte: 'montant',
  },
  {
    cle: 'decouvert_autorise',
    nom: 'Découvert autorisé',
    unite: '€',
    aide: 'trésorerie · à confirmer avec la banque',
    sorte: 'montant',
  },
];

type Saisie = Readonly<Record<Cle, string>>;

function versSaisie(r: ReglagesAnalyse): Saisie {
  const s = {} as Record<Cle, string>;
  for (const c of CHAMPS_REGLAGES) s[c.cle] = String(r[c.cle]).replace('.', ',');
  return s;
}

/** Contrôle la saisie ; rend le corps à envoyer (seulement ce qui change) ou une erreur. */
export function corpsDesReglages(
  saisie: Saisie,
  depart: ReglagesAnalyse,
): { corps: Record<string, number | string> } | { erreur: string } {
  const corps: Record<string, number | string> = {};
  for (const c of CHAMPS_REGLAGES) {
    const brut = saisie[c.cle].trim().replace(/\s/g, '').replace(',', '.');
    if (c.sorte === 'entier') {
      if (!/^\d+$/.test(brut)) return { erreur: `${c.nom} : nombre entier attendu` };
      const v = Number(brut);
      if (v < (c.min ?? 0) || v > (c.max ?? Infinity)) {
        return { erreur: `${c.nom} : de ${String(c.min)} à ${String(c.max)}` };
      }
      if (v !== depart[c.cle]) corps[c.cle] = v;
    } else {
      if (!/^\d{1,10}(\.\d{1,2})?$/.test(brut)) {
        return { erreur: `${c.nom} : montant positif, deux décimales au plus` };
      }
      if (c.max !== undefined && Number(brut) > c.max) {
        return { erreur: `${c.nom} : ${String(c.max)} au plus` };
      }
      if (Number(brut) !== Number(depart[c.cle])) corps[c.cle] = brut;
    }
  }
  return { corps };
}

export function PanneauReglages({
  jeton,
  quandAccesPerdu,
  messageRefus,
  quandEnregistre,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
  readonly quandEnregistre: () => void;
}) {
  const [depart, setDepart] = useState<ReglagesAnalyse | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    void (async () => {
      const j = await jeton();
      if (j === null) return quandAccesPerdu();
      const r = await lireReglagesAnalyse(j);
      if (r.etat === 'ok') setDepart(r.valeur);
      else {
        if (r.cause === 'acces') quandAccesPerdu();
        setRefus(r.message ?? messageRefus(r.cause));
      }
    })();
  }, [jeton]);

  async function enregistrer(corps: Record<string, number | string>) {
    setEnvoi(true);
    setMessage(null);
    const j = await jeton();
    if (j === null) {
      setEnvoi(false);
      return quandAccesPerdu();
    }
    const r = await enregistrerReglagesAnalyse(j, corps);
    setEnvoi(false);
    if (r.etat === 'ok') {
      setDepart(r.valeur);
      setRefus(null);
      setMessage('Réglages enregistrés.');
      quandEnregistre();
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? messageRefus(r.cause));
    }
  }

  return (
    <VueReglages
      depart={depart}
      refus={refus}
      message={message}
      envoi={envoi}
      quandEnregistrer={(c) => void enregistrer(c)}
    />
  );
}

export function VueReglages({
  depart,
  refus,
  message,
  envoi,
  quandEnregistrer,
}: {
  readonly depart: ReglagesAnalyse | null;
  readonly refus: string | null;
  readonly message: string | null;
  readonly envoi: boolean;
  readonly quandEnregistrer: (corps: Record<string, number | string>) => void;
}) {
  const [saisie, setSaisie] = useState<Saisie | null>(
    depart === null ? null : versSaisie(depart),
  );
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (depart !== null) setSaisie(versSaisie(depart));
  }, [depart]);

  if (depart === null || saisie === null) {
    return (
      <section className="reglages-analyse">
        {refus !== null ? (
          <p className="bandeau bandeau-refus">{refus}</p>
        ) : (
          <p className="attente">Lecture des réglages…</p>
        )}
      </section>
    );
  }

  const jamais = depart.modifie_le === null;
  return (
    <section className="reglages-analyse" aria-label="Réglages de l’analyse">
      <h3>Réglages de l’analyse</h3>
      <p className="ech-sous">
        {jamais
          ? 'Valeurs de départ du cadrage, jamais confirmées : les délais marqués « supposé » sont des hypothèses.'
          : `Modifiés par ${depart.modifie_par ?? '—'} le ${depart.modifie_le.slice(8, 10)}/${depart.modifie_le.slice(5, 7)}/${depart.modifie_le.slice(0, 4)}.`}
      </p>
      <form
        className="reglages-grille"
        onSubmit={(e) => {
          e.preventDefault();
          const r = corpsDesReglages(saisie, depart);
          if ('erreur' in r) return setErreur(r.erreur);
          setErreur(null);
          if (Object.keys(r.corps).length === 0)
            return setErreur('Aucun réglage n’a changé.');
          quandEnregistrer(r.corps);
        }}
      >
        {CHAMPS_REGLAGES.map((c) => (
          <label key={c.cle} className="ech-champ">
            <span>
              {c.nom}
              {jamais && c.aide.includes('supposé') && (
                <span className="ech-etiquette reglages-suppose">supposé</span>
              )}
            </span>
            <span className="reglages-saisie">
              <input
                inputMode={c.sorte === 'entier' ? 'numeric' : 'decimal'}
                value={saisie[c.cle]}
                onChange={(e) => setSaisie({ ...saisie, [c.cle]: e.target.value })}
              />
              <span className="ech-doux">{c.unite}</span>
            </span>
            <span className="ech-petit ech-doux">
              {c.aide.replace(/ ?· ?supposé|^supposé,? ?/, '') || ' '}
            </span>
          </label>
        ))}
        <div className="ech-actions reglages-pied">
          <button type="submit" className="bouton bouton-petit" disabled={envoi}>
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          {erreur !== null && <span className="ech-ko">{erreur}</span>}
          {refus !== null && <span className="ech-ko">{refus}</span>}
          {message !== null && erreur === null && (
            <span className="ech-ok">{message}</span>
          )}
        </div>
      </form>
    </section>
  );
}
